/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2026-03-13 19:20:14
 */

import React, { useCallback, useMemo, useRef } from 'react'
import { Drawer, Button } from 'antd'
import html2canvas from 'html2canvas'
import { v4 as uuid } from 'uuid'
import useVerifyAuthMethods from '../../useVerifyAuthMethods'
import { VerifyAuthResult } from '../../types'
import titleImage from '../../../../images/verify-auth/result_report_title.png'
import badgeImage from '../../../../images/verify-auth/badge.png'
import truthCertStampImage from '../../../../images/verify-auth/embossed_seal.png'
import { useAppSelector } from '../../../../app/hooks'
import { selectVerifyAuthConfig } from '../../../../slices/verifyAuthSlice'
import ContactUS from '../../../components/ContactUS'
import SelfBuiltLocationTag from './SelfBuiltLocationTag'

interface DetailDrawerProps {
  open: boolean
  onClose: () => void
  choosePhoto: string
  photoDetail: VerifyAuthResult
  reportSaClickEvent: (event: string) => void
}

export default ({
  open,
  onClose,
  photoDetail,
  choosePhoto,
  reportSaClickEvent
}: DetailDrawerProps) => {
  const captureRef = useRef(null)
  const config = useAppSelector(selectVerifyAuthConfig)
  const {
    exceptionResultCodes,
    isUnknownCheck,
    getUnKnowScene,
    isFakeCheck,
    getFakeScene,
    isTruthCheck,
    getTruthScene,
    getResultStatusImage,
    getResultStampImage,
    getFeature
  } = useVerifyAuthMethods()

  const isUnknown = useMemo(() => {
    return isUnknownCheck(photoDetail?.status)
  }, [photoDetail?.status, isUnknownCheck])

  const isFake = useMemo(() => {
    return isFakeCheck(photoDetail?.status)
  }, [photoDetail?.status, isFakeCheck])

  const unKnowScene = useMemo(() => {
    return getUnKnowScene(photoDetail?.status)
  }, [photoDetail?.status, getUnKnowScene])

  const fakeScene = useMemo(() => {
    return getFakeScene(photoDetail?.status)
  }, [photoDetail?.status, getFakeScene])

  const isTruth = useMemo(() => {
    return isTruthCheck(photoDetail?.status)
  }, [photoDetail?.status, isTruthCheck])

  const truthScene = useMemo(() => {
    return getTruthScene(photoDetail?.status)
  }, [photoDetail?.status, getTruthScene])

  const resultStatusImage = useMemo(() => {
    return getResultStatusImage(photoDetail?.status)
  }, [photoDetail?.status, getResultStatusImage])

  const resultStampImage = useMemo(() => {
    return getResultStampImage(photoDetail?.status)
  }, [photoDetail?.status, getResultStampImage])

  const truthCertStamp = useMemo(() => {
    if (isTruth) {
      if (config.truthConfigs.certStampImage) {
        return config.truthConfigs.certStampImage
      } else {
        return {
          url: truthCertStampImage,
          width: 113,
          height: 113,
          x: 176,
          y: 120
        }
      }
    }
    return null
  }, [photoDetail?.status, isTruth, config])

  const latLng = useMemo(() => {
    if (photoDetail?.PhotoLat && photoDetail?.PhotoLng) {
      return `${Number(photoDetail.PhotoLat).toFixed(6)}°N, ${Number(
        photoDetail.PhotoLng
      ).toFixed(6)}°E`
    }
    return '无'
  }, [photoDetail])

  const handleCaptureClick = async () => {
    if (captureRef.current) {
      const canvas = await html2canvas(captureRef.current, { useCORS: true })
      const dataUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = uuid() + '.png'
      document.body.appendChild(link) // 将链接临时添加到DOM
      link.click() // 触发下载
      document.body.removeChild(link)
    }
  }

  return (
    <Drawer
      placement="right"
      className="result-detail-drawer-v2"
      closable={true}
      onClose={onClose}
      open={open}
      width={423} // 375 + 24 + 24
    >
      <div className="drawer-content" ref={captureRef}>
        <img className="title-image" src={titleImage} alt="" />
        <div className="result-status-card">
          <img className="badge-image" src={badgeImage} alt="" />
          <img
            className="result-status-image"
            style={{
              marginBottom: 10,
              width: resultStatusImage.width,
              height: resultStatusImage.height
            }}
            src={resultStatusImage.url}
            alt=""
          />
          {isUnknown && (
            <div>
              <div
                className="summary-item"
                style={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <span
                  className="summary-title-text"
                  style={{ color: '#ff9f0A' }}
                >
                  {unKnowScene?.title}
                </span>
                {unKnowScene?.description && (
                  <div>
                    <span
                      className="summary-title-text"
                      style={{
                        marginTop: 4,
                        fontWeight: 400,
                        color: '#c3c3c3'
                      }}
                    >
                      {unKnowScene?.description}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {isFake && (
            <div>
              {fakeScene?.features.map((item, index) => {
                const feature = getFeature(item, photoDetail)
                return (
                  <div className="summary-item" key={index}>
                    <img className="feature-icon" src={feature.icon} alt="" />
                    <span
                      className="summary-title-text"
                      style={{ color: feature.color }}
                    >
                      <span className="left">{feature.title}：</span>
                      <span className="right">{feature.content}</span>
                    </span>
                  </div>
                )
              })}
            </div>
          )}
          {isTruth && (
            <div>
              {truthScene?.features.map((item, index) => {
                const feature = getFeature(item, photoDetail)
                return (
                  <div className="summary-item" key={index}>
                    <img className="feature-icon" src={feature.icon} alt="" />
                    <span
                      className="summary-title-text"
                      style={{ color: feature.color }}
                    >
                      <span className="left">{feature.title}：</span>
                      <span className="right">
                        {feature.content}
                        {feature.title === '地点真实' &&
                          photoDetail?.PhotoExt?.isSelfLocation === 'true' && (
                            <SelfBuiltLocationTag />
                          )}
                      </span>
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="photo-detail-card">
          <div className="photo-image-container">
            <img className="photo-image" src={choosePhoto} alt="" />
            <img
              className="result-stamp-image"
              src={resultStampImage.url}
              style={{
                width: resultStampImage.width,
                height: resultStampImage.height,
                left: resultStampImage.x,
                top: resultStampImage.y
              }}
              alt=""
            />
            {isTruth && (
              <img
                className="result-stamp-image"
                src={truthCertStamp!.url}
                style={{
                  width: truthCertStamp!.width,
                  height: truthCertStamp!.height,
                  left: truthCertStamp!.x,
                  top: truthCertStamp!.y
                }}
                alt=""
              />
            )}
          </div>

          <div className="photo-detail-table">
            <div className="photo-detail-table-cell">
              <span>
                应用：
                {photoDetail?.PhotoExt?.photoFrom === 'todayCam'
                  ? '今日水印相机'
                  : '非今日水印相机'}
              </span>
            </div>
            <div className="photo-detail-table-cell">
              <span>
                版本号：
                {photoDetail?.PhotoExt?.versionCode || '-'}
              </span>
            </div>
            <div className="photo-detail-table-cell">
              <span>
                尺寸：
                {photoDetail?.PhotoWideLength || '-'}
              </span>
            </div>

            <div className="photo-detail-table-cell">
              <span>经纬度：{latLng}</span>
            </div>
            <div className="photo-detail-table-cell">
              <span>
                大小：
                {photoDetail?.PhotoSize || '-'}
              </span>
            </div>
            {photoDetail?.PhotoExt?.serverDateTime && (
              <div className="photo-detail-table-cell">
                <span>验真时间：{photoDetail?.PhotoExt?.serverDateTime}</span>
              </div>
            )}
            <div className="photo-detail-table-cell">
              <span>格式：{photoDetail?.PhotoType}</span>
            </div>
            <div className="photo-detail-table-cell">
              <span>防伪码：{photoDetail?.PhotoExt?.antiFakeCode}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bottom">
        {isUnknown ? (
          exceptionResultCodes.includes(photoDetail?.status) ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                onClick={() => {
                  reportSaClickEvent('clickErrorPopKnow')
                  handleCaptureClick()
                }}
              >
                下载
              </Button>
              <ContactUS fromPage="Web验真检测出现异常">
                <Button
                  className="contact-us-btn"
                  onClick={() => {
                    reportSaClickEvent('cickContactUs')
                  }}
                >
                  联系客服
                </Button>
              </ContactUS>
            </div>
          ) : (
            <Button
              style={{
                background: 'rgba(32, 122, 255, 1)',
                color: '#fff'
              }}
              onClick={() => {
                reportSaClickEvent('clickErrorPopKnow')
                handleCaptureClick()
              }}
            >
              下载
            </Button>
          )
        ) : (
          <Button
            style={{ background: 'rgba(32, 122, 255, 1)', color: '#fff' }}
            onClick={() => {
              if (isTruth) {
                reportSaClickEvent('clickPassPopDownload')
              } else if (isFake) {
                reportSaClickEvent('clickFalsePopDownload')
              }
              handleCaptureClick()
            }}
          >
            下载
          </Button>
        )}
      </div>
    </Drawer>
  )
}
